<div class="media-upload-dialog" [class.inline-mode]="!isDialogMode">
  <!-- Dialog Header -->
  @if (isDialogMode) {
    <div class="dialog-header">
      <div class="header-icon">
        <mat-icon>cloud_upload</mat-icon>
      </div>
      <h2>{{ dialogTitle }}</h2>
      <button class="close-btn" (click)="cancel()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  }

  <!-- Dialog Content -->
  <div class="dialog-content">
    <!-- Drop Zone -->
    <div class="drop-zone"
      [class.dragging]="isDragging"
      [class.has-files]="files.length > 0"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)"
      (drop)="onDrop($event)">

      @if (files.length === 0) {
        <div class="drop-zone-content">
          <mat-icon class="upload-icon">cloud_upload</mat-icon>
          <h3>Drag & Drop Files Here</h3>
          <p>or</p>
          <button class="btn-browse" (click)="fileInput.click()">
            <mat-icon>upload</mat-icon>
            Browse Files
          </button>
          <p class="file-types">{{ supportedFilesText }}</p>
        </div>
      }

      <!-- File List -->
      @if (files.length > 0) {
        <div class="file-list">
          <div class="file-stats">
            <span>{{ files.length }} file(s) selected</span>
            <button class="btn-add-more" (click)="fileInput.click()" [disabled]="isUploading" [hidden]="isReading">
              <mat-icon>add</mat-icon>
              Add More
            </button>
          </div>
          <div class="file-items">
            @for (file of files; track file; let i = $index) {
              <div class="file-item"
                [class.uploading]="file.status === 'uploading'"
                [class.success]="file.status === 'success'"
                [class.error]="file.status === 'error'">
                <!-- Preview -->
                <div class="file-preview">
                  @if (file.preview) {
                    <img [src]="file.preview" [alt]="file.file.name">
                  }
                  @if (!file.preview) {
                    <mat-icon class="file-icon">{{ getFileIcon(file.file) }}</mat-icon>
                  }
                </div>
                <!-- File Info -->
                <div class="file-info">
                  <div class="file-name">{{ file.file.name }}</div>
                  <div class="file-size">{{ getFileSize(file.file.size) }}</div>
                  <!-- Progress Bar -->
                  @if (file.status === 'uploading') {
                    <div class="file-progress">
                      <mat-progress-bar mode="determinate" [value]="file.progress"></mat-progress-bar>
                    </div>
                  }
                  <!-- Status -->
                  <div class="file-status">
                    @if (file.status === 'pending') {
                      <span class="status-pending">
                        <mat-icon>schedule</mat-icon> Pending
                      </span>
                    }
                    @if (file.status === 'uploading') {
                      <span class="status-uploading">
                        <mat-icon>upload</mat-icon> Uploading...
                      </span>
                    }
                    @if (file.status === 'success') {
                      <span class="status-success">
                        <mat-icon>check_circle</mat-icon> Uploaded
                      </span>
                    }
                    @if (file.status === 'error') {
                      <span class="status-error">
                        <mat-icon>error</mat-icon> {{ file.error || 'Failed' }}
                      </span>
                    }
                  </div>
                </div>
                <!-- Remove Button -->
                <button mat-icon-button
                  class="remove-button"
                  (click)="removeFile(i)"
                  [disabled]="(isUploading && file.status === 'uploading') || isReading"
                  matTooltip="Remove file">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
          <!-- Reading mode: step checklist -->
          @if (isReadingMode && readingIndex >= 0) {
            <div class="reading-steps" aria-live="polite">
              @for (step of readingStages || []; track step.stage; let i = $index) {
                <div class="reading-step" [ngClass]="readingStepState(i)">
                  <span class="dot">
                    @if (readingStepState(i) === 'done') { <mat-icon>check</mat-icon> }
                  </span>
                  {{ step.label }}
                </div>
              }
              <!--
                Sets the expectation. The read is a server-side call that takes a minute or two on
                a real document, and on a synchronous queue the steps above cannot tick while it
                runs — so without this the dialog looks frozen rather than busy.
              -->
              @if (readingHintText) {
                <p class="reading-hint">{{ readingHintText }}</p>
              }
            </div>
          }
        </div>
      }
    </div>

    <!-- Hidden File Input -->
    <input type="file"
      #fileInput
      multiple
      [accept]="acceptedFileTypes"
      style="display: none"
      (change)="onFileSelect($event)">
  </div>

  <!-- Dialog Footer -->
  @if (isDialogMode) {
    <div class="dialog-footer">
      @if (isUploading) {
        <div class="upload-stats">
          <mat-spinner diameter="20"></mat-spinner>
          <span>Uploading: {{ uploadedFiles }} / {{ totalFiles }}</span>
          @if (failedFiles > 0) {
            <span class="failed-count">{{ failedFiles }} failed</span>
          }
        </div>
      }
      @if (isReading) {
        <div class="upload-stats">
          <mat-spinner diameter="20"></mat-spinner>
          <span>{{ readingFooterText }}</span>
        </div>
      } @else if (!isUploading) {
        <div class="upload-stats">
          <!-- Empty div to maintain layout spacing -->
        </div>
      }
      <div class="dialog-actions">
        @if (isReading) {
          <button class="btn-cancel" (click)="cancel()">
            <mat-icon>close</mat-icon>
            Cancel
          </button>
        } @else {
        <button class="btn-cancel" (click)="cancel()" [disabled]="isUploading">
          <mat-icon>close</mat-icon>
          Cancel
        </button>
        <button class="btn-submit"
          (click)="uploadFiles()"
          [disabled]="files.length === 0 || isUploading">
          @if (isUploading) {
            <mat-spinner diameter="20" class="upload-spinner"></mat-spinner>
          }
          @if (!isUploading) {
            <mat-icon>cloud_upload</mat-icon>
          }
          <span>{{ isUploading ? 'Uploading...' : 'Upload All' }}</span>
        </button>
        }
      </div>
    </div>
  }

  <!-- Inline Mode Upload Button -->
  @if (!isDialogMode && files.length > 0) {
    <div class="inline-upload-button">
      <button mat-raised-button
        color="primary"
        (click)="uploadFiles()"
        [disabled]="isUploading">
        <mat-icon>cloud_upload</mat-icon>
        {{ isUploading ? 'Uploading...' : 'Upload ' + files.length + ' file(s)' }}
      </button>
      @if (isUploading) {
        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      }
    </div>
  }
</div>
